import { AppStore } from "@/store/AppStore";
import { GatedFeature } from "@/store/feature-gates/types";
import {
  PaywallContext,
  type FeatureGateDeniedEventParams,
  type FeatureGateEventParams,
  type FeatureGateLevel,
  type PaywallEventParams,
} from "@/domains/metrics/tracked-event";

export interface MonetizationUsagePayload {
  note_create_quota_used: number;
  note_create_quota_limit: number | typeof Infinity;
  note_create_usage_ratio: number;
  chat_message_send_quota_used: number;
  chat_message_send_quota_limit: number | typeof Infinity;
  chat_message_send_chat_usage_ratio: number;
  content_understanding_pdf_page_quota_used: number;
  content_understanding_pdf_page_quota_limit: number | typeof Infinity;
  content_understanding_pdf_page_usage_ratio: number;
}

type GateCheckResult = ReturnType<AppStore["featureGates"]["checkGate"]>;

export function buildMonetizationUsagePayload(store: AppStore): MonetizationUsagePayload {
  const noteGate = store.featureGates.checkGate(GatedFeature.NoteCreate);
  const chatGate = store.featureGates.checkGate(GatedFeature.ChatMessageSend);
  const pdfGate = store.featureGates.checkGate(GatedFeature.ContentUnderstandingPdfPage);

  const safeRatio = (used: number, limit: number | typeof Infinity) =>
    limit === Infinity || limit === 0 ? 0 : used / (limit as number);

  return {
    note_create_quota_used: noteGate.used,
    note_create_quota_limit: noteGate.limit,
    note_create_usage_ratio: safeRatio(noteGate.used, noteGate.limit),
    chat_message_send_quota_used: chatGate.used,
    chat_message_send_quota_limit: chatGate.limit,
    chat_message_send_chat_usage_ratio: safeRatio(chatGate.used, chatGate.limit),
    content_understanding_pdf_page_quota_used: pdfGate.used,
    content_understanding_pdf_page_quota_limit: pdfGate.limit,
    content_understanding_pdf_page_usage_ratio: safeRatio(pdfGate.used, pdfGate.limit),
  };
}

const toFeatureGateEventPayload = ({
  feature,
  gate,
  level,
}: {
  feature: GatedFeature;
  gate: GateCheckResult;
  level: FeatureGateLevel;
}): FeatureGateEventParams => {
  return {
    feature,
    level,
    used: gate.used,
    limit: gate.limit,
    warn_at: gate.warnAt,
  };
};

/**
 * Picks the feature to route the banner click to. When multiple limits are
 * exceeded at once, `modalFeature` is undefined so the upsell modal renders
 * the generic "monthly usage limits" copy instead of the per-feature copy.
 * `eventFeature` is always a concrete feature for analytics continuity.
 */
export const resolveActiveBannerFeatureGate = ({
  store,
}: {
  store: AppStore;
}): {
  modalFeature: GatedFeature | undefined;
  eventFeature: GatedFeature;
  gate: GateCheckResult;
} => {
  const noteGate = store.featureGates.checkGate(GatedFeature.NoteCreate);
  const chatGate = store.featureGates.checkGate(GatedFeature.ChatMessageSend);
  const isPdfPaywallEnabled = store.spaceAccountFeatureFlags.getFlagStatus("pdf-paywall") ?? false;
  const pdfGate = isPdfPaywallEnabled
    ? store.featureGates.checkGate(GatedFeature.ContentUnderstandingPdfPage)
    : null;

  const exceeded: { feature: GatedFeature; gate: GateCheckResult }[] = [];
  if (!noteGate.allowed) exceeded.push({ feature: GatedFeature.NoteCreate, gate: noteGate });
  if (!chatGate.allowed) exceeded.push({ feature: GatedFeature.ChatMessageSend, gate: chatGate });
  if (pdfGate && !pdfGate.allowed) {
    exceeded.push({ feature: GatedFeature.ContentUnderstandingPdfPage, gate: pdfGate });
  }

  if (exceeded.length > 1) {
    return { modalFeature: undefined, eventFeature: exceeded[0].feature, gate: exceeded[0].gate };
  }

  if (exceeded.length === 1) {
    const only = exceeded[0];
    return { modalFeature: only.feature, eventFeature: only.feature, gate: only.gate };
  }

  const isAtWarn = (g: GateCheckResult) => g.warnAt !== null && g.used >= g.warnAt;

  if (isAtWarn(noteGate)) {
    return {
      modalFeature: GatedFeature.NoteCreate,
      eventFeature: GatedFeature.NoteCreate,
      gate: noteGate,
    };
  }

  if (isAtWarn(chatGate)) {
    return {
      modalFeature: GatedFeature.ChatMessageSend,
      eventFeature: GatedFeature.ChatMessageSend,
      gate: chatGate,
    };
  }

  if (pdfGate && isAtWarn(pdfGate)) {
    return {
      modalFeature: GatedFeature.ContentUnderstandingPdfPage,
      eventFeature: GatedFeature.ContentUnderstandingPdfPage,
      gate: pdfGate,
    };
  }

  return {
    modalFeature: GatedFeature.NoteCreate,
    eventFeature: GatedFeature.NoteCreate,
    gate: noteGate,
  };
};

export function buildFeatureGateBannerEventPayload({
  store,
  level,
}: {
  store: AppStore;
  level: FeatureGateLevel;
}): FeatureGateEventParams {
  const { eventFeature, gate } = resolveActiveBannerFeatureGate({ store });
  return toFeatureGateEventPayload({ feature: eventFeature, gate, level });
}

export function buildPaywallEventPayload({
  store,
  context,
  feature,
  level,
}: {
  store: AppStore;
  context?: string;
  feature?: GatedFeature;
  level?: FeatureGateLevel;
}): PaywallEventParams {
  const monetizationUsagePayload = buildMonetizationUsagePayload(store);
  const shouldIncludeFeatureGatePayload = Boolean(
    feature &&
      level &&
      feature !== GatedFeature.DarkMode &&
      context !== PaywallContext.ChatModelSelection &&
      context !== PaywallContext.DarkModeToggle
  );

  if (!shouldIncludeFeatureGatePayload || !feature || !level) {
    return {
      context,
      ...monetizationUsagePayload,
    };
  }

  return {
    context,
    ...monetizationUsagePayload,
    ...toFeatureGateEventPayload({
      feature,
      gate: store.featureGates.checkGate(feature),
      level,
    }),
  };
}

export function buildFeatureGateDeniedEventPayload({
  feature,
  context,
  gate,
}: {
  feature: GatedFeature;
  context?: string;
  gate: GateCheckResult;
}): FeatureGateDeniedEventParams {
  return {
    context,
    ...toFeatureGateEventPayload({
      feature,
      gate,
      level: "exceeded",
    }),
  };
}
